import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash'
import { FormattedMessage } from 'react-intl';

import { sub } from 'date-fns'
import { zhTW } from "date-fns/locale";

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import FormHelperText from '@mui/material/FormHelperText';

import ModalHeader from '../pop/ModalHeader';
import ModalFooter from '../pop/ModalFooter';

import theme from '../../theme';

import useGlobalStatus from '../../hook/useGlobalStatus';
import useColorSystem from '../../hook/useColorSystem';

import { DateRangeOutlinedIcon, CorporateFareIcon, GroupsOutlinedIcon } from '../../constants/images';

import { handleDateRangeChange, dateRangeList } from '../../utils/date';

import { checkAddressesIsNull, checkDateRangeForm } from '../formData/formData';

import { BoxModalWrapper } from '../customComponent';

import { rangeShortcuts } from './data/sendDetailConstants';


const SendEmailModal = (props) => {
  const {
    media,
    open,
    handleClose,
    reportDateRange,
    setReportDateRange,
    reportMode,
    setReportMode,
    fetchSendAllEmails,
    downloadInputErrorMsg,
    setDownloadInputErrorMsg,
  } = props;

  const { currentActiveData, locale } = useGlobalStatus();
  const { colorMedia, mainColor } = useColorSystem();

  const handleReportDateChange = (target, value) => {
    setReportDateRange((prev) => {
      const updatedRange = {
        ...prev,
        [target]: value
      };

      const { startReportDate, endReportDate } = updatedRange;

      const predefinedRanges = {
        last_7d: dateRangeList.last_7d.getRange(),
        last_14d: dateRangeList.last_14d.getRange(),
        last_month: dateRangeList.last_month.getRange()
      };

      const matchingMode = Object.entries(predefinedRanges).find(([key, [start, end]]) => {
        return (
          startReportDate?.toDateString() === start.toDateString() &&
          endReportDate?.toDateString() === end.toDateString()
        );
      });

      setReportMode(matchingMode ? matchingMode[0] : 'custom');

      return updatedRange;
    });
  }

  const handleRangeClick = (mode) => {
    const [startDate, endDate] = handleDateRangeChange(mode);
    setReportMode(mode);
    setReportDateRange({
      startReportDate: startDate,
      endReportDate: endDate
    })
  }

  const handleSendEmail = () => {
    const errorMsg = checkDateRangeForm(reportDateRange);
    setDownloadInputErrorMsg({ reportDateRange: errorMsg });
    if (errorMsg) return;

    fetchSendAllEmails();
  }

  return (
    <>
      {
        <Modal
          open={open}
          onClose={handleClose}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <BoxModalWrapper sx={{ width: '45%' }}>
            <ModalHeader
              title={<FormattedMessage id="sendList.detail.button.sendAllEmails" />}
              handleClose={handleClose}
            />
            <Box
              sx={{
                alignItems: 'center',
                width: '100%',
                padding: '30px 40px 50px'
              }}
            >
              <Stack variant="start-start" mb={3}>
                <CorporateFareIcon variant="grey_mt2" />
                <Typography sx={{ maxWidth: '95%' }}>{currentActiveData.weeklyReportName}</Typography>
              </Stack>

              <Stack variant="start-start" mb={3}>
                <GroupsOutlinedIcon variant="grey_mt2" />
                {checkAddressesIsNull(currentActiveData.toAddresses) ?
                  (<Typography sx={{ color: `${theme.palette.grey.w400}` }}><FormattedMessage id="sendList.detail.unsetEmail" /></Typography>
                  ) : (
                    <List disablePadding dense>
                      {currentActiveData.toAddresses.map(el => {
                        return (
                          <ListItem key={el} disablePadding>
                            <ListItemText
                              primary={<Typography>{el}</Typography>}
                              sx={{ m: 0 }}
                            />
                          </ListItem>
                        )
                      })}
                    </List>
                  )
                }
              </Stack>

              <Stack variant="start-start" mb={2}>
                <DateRangeOutlinedIcon variant="grey_mt2" />
                <Typography><FormattedMessage id="sendTime.sendType.deliveryCycle" /></Typography>
              </Stack>

              <Box
                sx={{
                  margin: "16px 0px 16px",
                  display: "flex",
                  justfyContent: "start",
                  wudth: '100%'
                }} >
                <ButtonGroup
                  variant="text"
                  aria-label="text button group"
                  color={colorMedia}
                >
                  {rangeShortcuts.map((el) => {
                    const currentLanguage = locale.includes('en') ? 'en' : 'zh';
                    return (
                      <Button
                        key={el.range}
                        color={reportMode === el?.range ? colorMedia : 'grey'}
                        onClick={() => handleRangeClick(el.range)}
                      >
                        <FormattedMessage id={el.labels} />
                      </Button>
                    )
                  })}
                </ButtonGroup>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }} >
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale.includes("en") ? null : zhTW}>
                  <DatePicker
                    value={reportDateRange.startReportDate}
                    inputFormat="yyyy/MM/dd"
                    views={['year', 'month', 'day']}
                    maxDate={sub(new Date(), { days: 1 })}
                    onChange={(value) => {
                      handleReportDateChange('startReportDate', value)
                    }}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          sx={{
                            width: '50%',
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused fieldset': {
                                borderColor: mainColor,
                              }
                            }
                          }}
                        />
                      )
                    }}
                  />
                  <Typography ml={1} mr={1}> - </Typography>
                  <DatePicker
                    value={reportDateRange.endReportDate}
                    inputFormat="yyyy/MM/dd"
                    views={['year', 'month', 'day']}
                    maxDate={sub(new Date(), { days: 1 })}
                    onChange={(value) => {
                      handleReportDateChange('endReportDate', value)
                    }}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          sx={{
                            width: '50%',
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused fieldset': {
                                borderColor: mainColor,
                              }
                            }
                          }}
                        />
                      )
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <FormHelperText error sx={{ fontSize: 14 }}>{downloadInputErrorMsg.reportDateRange}</FormHelperText>
            </Box>

            <ModalFooter
              checkBottonContent={<FormattedMessage id="sendList.detail.sendAllEmail.button.confirm" />}
              handelCancel={handleClose}
              handleSubmit={handleSendEmail}
            />
          </BoxModalWrapper>
        </Modal>
      }
    </>
  )
}

export default SendEmailModal;